// ============================================================================
// Google Calendar — calendar list helpers.
//
// `GET /calendar/v3/users/me/calendarList` returns the set of calendars the
// authenticated user has in their sidebar. We map Google's raw shape into the
// capability-level `Calendar[]` so adapter consumers stay provider-agnostic.
//
// Pagination gotcha: Google pages calendarList at 100 per call by default.
// `listGoogleCalendars` follows `nextPageToken` transparently and returns
// the full list. Callers that need streaming / cursor semantics should use
// `listGoogleCalendarsPage` instead and paginate themselves.
//
// Canonical docs:
//   - https://developers.google.com/calendar/api/v3/reference/calendarList/list
// ============================================================================

import type { Calendar } from '../../capabilities/calendar'
import {
  GOOGLE_CALENDAR_API_BASE,
} from './oauth'
import {
  GoogleCalendarError,
  classifyGoogleError,
  parseGoogleRetryAfterMs,
  type GoogleCalendarListResponse,
  type GoogleCalendarRaw,
  type GoogleErrorEnvelope,
} from './types'

// ============================================================================
// Constants
// ============================================================================

/** Per-page cap for calendarList.list (Google's default is 100; max is 250). */
const CALENDAR_LIST_PAGE_SIZE = 250
/** Hard ceiling on total calendars we'll collect per call — defence against a
 *  pathological account. 2,500 is well beyond any real user's sidebar. */
const CALENDAR_LIST_HARD_CAP = 2500

// ============================================================================
// listGoogleCalendars
// ============================================================================

export interface ListCalendarsPageResult {
  calendars: Calendar[]
  nextPageToken?: string
}

/**
 * List one page of calendars. Returns the raw `nextPageToken` so the caller
 * can control pagination — useful for tests and for admin tooling that
 * wants to chunk large lists.
 */
export async function listGoogleCalendarsPage(
  accessToken: string,
  pageToken: string | undefined = undefined,
  fetchImpl: typeof fetch = fetch,
): Promise<ListCalendarsPageResult> {
  assertAccessToken(accessToken)

  const url = new URL(`${GOOGLE_CALENDAR_API_BASE}/users/me/calendarList`)
  url.searchParams.set('maxResults', String(CALENDAR_LIST_PAGE_SIZE))
  if (pageToken) url.searchParams.set('pageToken', pageToken)

  const resp = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      // Token goes in the Authorization header — NEVER the URL — so Cloudflare
      // edge + proxy logs don't capture it.
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!resp.ok) {
    throw await errorFromResponse(resp, 'calendar')
  }

  const body = (await resp.json().catch(() => null)) as
    | GoogleCalendarListResponse
    | null
  if (!body) {
    throw new GoogleCalendarError(
      'PROVIDER_ERROR',
      'Google calendarList.list returned an unexpected body',
    )
  }

  const items = Array.isArray(body.items) ? body.items : []
  const calendars = items.map(mapCalendar)

  return {
    calendars,
    nextPageToken: typeof body.nextPageToken === 'string' ? body.nextPageToken : undefined,
  }
}

/**
 * Fetch EVERY calendar in the account, transparently following pagination.
 * Bounded at `CALENDAR_LIST_HARD_CAP` to avoid unbounded memory growth on a
 * runaway account.
 */
export async function listGoogleCalendars(
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Calendar[]> {
  const out: Calendar[] = []
  let pageToken: string | undefined = undefined
  // Defence: cap the number of pages in case Google returns a cyclic cursor
  // (shouldn't happen, but gives a bounded failure mode).
  const maxPages = Math.ceil(CALENDAR_LIST_HARD_CAP / CALENDAR_LIST_PAGE_SIZE) + 1
  for (let i = 0; i < maxPages; i++) {
    const page = await listGoogleCalendarsPage(accessToken, pageToken, fetchImpl)
    for (const cal of page.calendars) {
      if (out.length >= CALENDAR_LIST_HARD_CAP) break
      out.push(cal)
    }
    if (!page.nextPageToken || out.length >= CALENDAR_LIST_HARD_CAP) break
    pageToken = page.nextPageToken
  }
  return out
}

// ============================================================================
// Mapping
// ============================================================================

function mapCalendar(raw: GoogleCalendarRaw): Calendar {
  const role = typeof raw.accessRole === 'string' ? raw.accessRole : undefined
  const out: Calendar = {
    id: raw.id,
    summary: typeof raw.summary === 'string' ? raw.summary : '',
    timeZone: typeof raw.timeZone === 'string' ? raw.timeZone : 'UTC',
  }
  if (typeof raw.description === 'string') out.description = raw.description
  if (raw.primary === true) out.primary = true
  if (
    role === 'owner' ||
    role === 'reader' ||
    role === 'writer' ||
    role === 'freeBusyReader'
  ) {
    out.accessRole = role
  }
  return out
}

// ============================================================================
// Internals
// ============================================================================

async function errorFromResponse(
  resp: Response,
  resourceKind: 'calendar' | 'event' | 'watch' | 'other',
): Promise<GoogleCalendarError> {
  let envelope: GoogleErrorEnvelope | null = null
  try {
    envelope = (await resp.json()) as GoogleErrorEnvelope
  } catch {
    // Non-JSON error body (Google gateway HTML etc.) — leave envelope null.
  }
  const retryAfterMs = parseGoogleRetryAfterMs(resp.headers.get('Retry-After'))
  return classifyGoogleError(resp.status, envelope, resourceKind, retryAfterMs)
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new GoogleCalendarError('INVALID_INPUT', 'accessToken is required')
  }
}
